import { Users } from "lucide-react";
import { Card, CardContent, CardHeader } from "../../ui/card";
import type { ProAudiencePreviewRender } from "../../../lib/pro-agent-types";

// preview_campaign_audience read widget (SILL-454) — an audience-count card for
// a candidate campaign filter. Counts only (matched / eligible + exclusion
// reasons), never recipient names or contact details (the D15 counts-only-PII
// boundary), so the model can't inflate reach or leak PII. Read-only.

// reasonLabel humanizes an eligibility-reason wire key (e.g. "muted" →
// "Muted", "no_email" → "No email").
function reasonLabel(reason: string): string {
  const spaced = reason.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export function AudiencePreviewCard({
  card,
}: {
  card: ProAudiencePreviewRender;
}) {
  const excluded = Object.entries(card.excluded_by_reason ?? {}).filter(
    ([, n]) => n > 0,
  );

  return (
    <Card data-testid="audience-preview-card">
      <CardHeader className="gap-1 pb-2">
        <div className="flex items-center gap-1.5">
          <Users size={13} aria-hidden className="text-fg-muted" />
          <p className="text-sm font-semibold text-fg">Audience preview</p>
        </div>
      </CardHeader>

      <CardContent className="pt-0">
        <div className="flex gap-3 border-t border-border-soft pt-2.5">
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold tabular-nums text-fg">
              {card.eligible}
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
              Eligible
            </p>
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-lg font-semibold tabular-nums text-fg-muted">
              {card.matched}
            </p>
            <p className="text-[11px] font-semibold uppercase tracking-wide text-fg-muted">
              Matched
            </p>
          </div>
        </div>

        {excluded.length > 0 ? (
          <ul
            data-testid="audience-preview-excluded"
            className="mt-2 space-y-0.5 border-t border-border-soft pt-2"
          >
            {excluded.map(([reason, n]) => (
              <li
                key={reason}
                className="flex items-center justify-between text-[11px] text-fg-muted"
              >
                <span>{reasonLabel(reason)}</span>
                <span className="tabular-nums">{n} excluded</span>
              </li>
            ))}
          </ul>
        ) : null}
      </CardContent>
    </Card>
  );
}
